import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { data } from '@/data';
import SEO from '@/components/SEO';
import { getPageSEO } from '@/config/seoConfig';
import { useTranslation } from '@/hooks/useTranslation';

const Gallery = () => {
  const seoData = getPageSEO('gallery');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const images = data.gallery;
  const t = useTranslation();

  const handlePrevious = () => {
    setSelectedIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  };

  const handleNext = () => {
    setSelectedIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
  };

  const handleThumbnailClick = (index) => {
    setSelectedIndex(index);
  };

  return (
    <>
      <SEO 
        title={seoData.title}
        description={seoData.description}
        keywords={seoData.keywords}
        url={seoData.url}
        image={seoData.image}
        type="website"
      />
      <div className="min-h-screen bg-gradient-to-br from-amber-50/40 to-orange-50/20 pt-32 pb-20">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="font-playfair text-4xl md:text-5xl lg:text-6xl mb-16 text-gray-800 text-center font-semibold tracking-tight"
        >
          {t.gallery.title}
        </motion.h1>

        {/* Main Image Display */}
        <div className="relative mb-12">
          <div className={`relative overflow-hidden bg-gray-100 ${
            images[selectedIndex].id === 5 
              ? 'aspect-[4/3] md:aspect-[16/10] flex items-center justify-center' 
              : 'aspect-[16/10]'
          }`}>
            <AnimatePresence mode="wait">
              <motion.img
                key={selectedIndex}
                src={images[selectedIndex].url}
                alt={images[selectedIndex].alt}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.5 }}
                className={images[selectedIndex].id === 5 ? "w-full h-full object-contain" : "w-full h-full object-cover"}
              />
            </AnimatePresence>
          </div>

          {/* Navigation Arrows */}
          <button
            onClick={handlePrevious}
            className="absolute left-2 md:left-4 top-1/2 -translate-y-1/2 bg-black/40 hover:bg-black/60 text-white rounded-full p-1.5 md:p-2 shadow-md transition-all duration-300 hover:scale-105 backdrop-blur-sm"
            aria-label={t.gallery.previousImage}
          >
            <ChevronLeft className="w-4 h-4 md:w-5 md:h-5" />
          </button>
          <button
            onClick={handleNext}
            className="absolute right-2 md:right-4 top-1/2 -translate-y-1/2 bg-black/40 hover:bg-black/60 text-white rounded-full p-1.5 md:p-2 shadow-md transition-all duration-300 hover:scale-105 backdrop-blur-sm"
            aria-label={t.gallery.nextImage}
          >
            <ChevronRight className="w-4 h-4 md:w-5 md:h-5" />
          </button>

          {/* Image Counter */}
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/70 text-white px-4 py-2 rounded-full text-sm">
            {selectedIndex + 1} / {images.length}
          </div>
        </div>

        {/* Thumbnail Grid */}
        <div className="grid grid-cols-5 sm:grid-cols-6 md:grid-cols-6 lg:grid-cols-8 gap-2 sm:gap-3">
          {images.map((image, index) => (
            <motion.button
              key={image.id}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3, delay: index * 0.02 }}
              onClick={() => handleThumbnailClick(index)}
              className={`relative aspect-square overflow-hidden cursor-pointer transition-all duration-300 ${
                image.id === 5 ? 'bg-gray-100 flex items-center justify-center' : ''
              } ${
                selectedIndex === index
                  ? 'ring-4 ring-gray-900 ring-offset-2 scale-95'
                  : 'hover:ring-2 hover:ring-gray-400 hover:ring-offset-2 opacity-70 hover:opacity-100'
              }`}
            >
              <img
                src={image.url}
                alt={image.alt}
                loading="lazy"
                className={image.id === 5 ? "max-w-full max-h-full object-contain" : "w-full h-full object-cover"}
              />
            </motion.button>
          ))}
        </div>
      </div>
    </div>
    </>
  );
};

export default Gallery;